import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection, type ILogger } from '@microsoft/signalr';
import { API_URL, getFreshAccessToken } from './client';
import { qk } from './queryKeys';
import type { BoardChangedEvent, Guid, MessageEventDto, NotificationDto } from './types';

export type LiveState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

let shared: HubConnection | null = null;
let startPromise: Promise<void> | null = null;
let refCount = 0;
const stateListeners = new Set<(s: LiveState) => void>();
const joinedCycles = new Map<Guid, number>();
let stopTimer: ReturnType<typeof setTimeout> | null = null;
/** Grace period before closing an unused connection: StrictMode remounts and quick navigations reuse it. */
const STOP_DELAY_MS = 2_000;

/** Leaving the page aborts the hub's requests; those are not errors worth reporting. */
let unloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => (unloading = true));
  window.addEventListener('beforeunload', () => (unloading = true));
  window.addEventListener('pageshow', () => (unloading = false));
}
const liveLogger: ILogger = {
  log(level, message) {
    if (unloading || level < LogLevel.Warning) return;
    if (level >= LogLevel.Error) console.error('[live]', message);
    else console.warn('[live]', message);
  },
};

function notify(s: LiveState) {
  stateListeners.forEach((l) => l(s));
}

function getConnection(): HubConnection {
  if (shared) return shared;
  shared = new HubConnectionBuilder()
    .withUrl(`${API_URL}/hubs/live`, {
      // SignalR passes this as `?access_token=` for WebSockets/SSE.
      accessTokenFactory: async () => (await getFreshAccessToken()) ?? '',
    })
    .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 20_000, 30_000])
    .configureLogging(liveLogger)
    .build();
  shared.onreconnecting(() => notify('reconnecting'));
  shared.onreconnected(() => {
    notify('connected');
    // Groups are per connection: re-join after a reconnect.
    for (const cycleId of joinedCycles.keys()) void shared?.invoke('JoinCycle', cycleId).catch(() => undefined);
  });
  shared.onclose(() => notify('disconnected'));
  return shared;
}

async function ensureStarted(): Promise<HubConnection> {
  const conn = getConnection();
  if (conn.state === HubConnectionState.Connected) return conn;
  if (!startPromise) {
    notify('connecting');
    startPromise = conn
      .start()
      .then(() => notify('connected'))
      .catch((e) => {
        notify('disconnected');
        throw e;
      })
      .finally(() => {
        startPromise = null;
      });
  }
  await startPromise;
  return conn;
}

/** Drops the connection at once (sign-in/out): the next one connects with the new person's token and channel. */
export function resetLiveConnection() {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  const c = shared;
  shared = null;
  void c?.stop();
}

/** Keeps the shared connection open while someone uses it; closes it a little after the last user leaves. */
function acquire() {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  refCount++;
}

function release() {
  refCount--;
  if (refCount > 0) return;
  refCount = 0;
  if (stopTimer) clearTimeout(stopTimer);
  stopTimer = setTimeout(() => {
    stopTimer = null;
    if (refCount > 0) return;
    const c = shared;
    shared = null;
    void c?.stop();
  }, STOP_DELAY_MS);
}

/**
 * The signed-in person's own channel: new notifications and changed unread counts (bell, inbox).
 * The server adds every connection to the user's group, so nothing needs joining.
 */
export function useUserLive(enabled: boolean, onNotification?: (n: NotificationDto) => void) {
  const qc = useQueryClient();
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const handlerRef = useRef(onNotification);
  handlerRef.current = onNotification;

  useEffect(() => {
    if (!enabled) return;
    acquire();
    const conn = getConnection();
    const refresh = () => void qcRef.current.invalidateQueries({ queryKey: qk.inbox.all });
    const onNote = (n: NotificationDto) => {
      refresh();
      handlerRef.current?.(n);
    };
    const onMessage = (evt: MessageEventDto) => {
      refresh();
      const client = qcRef.current;
      void client.invalidateQueries({ queryKey: qk.messages.conversations });
      void client.invalidateQueries({ queryKey: qk.messages.thread(evt.conversationId) });
    };
    conn.on('notification', onNote);
    conn.on('countsChanged', refresh);
    conn.on('message', onMessage);
    ensureStarted().catch(() => undefined);
    return () => {
      conn.off('notification', onNote);
      conn.off('countsChanged', refresh);
      conn.off('message', onMessage);
      release();
    };
  }, [enabled]);
}

/**
 * Join a cycle's live group and invalidate board/review queries on `boardChanged`.
 * Falls back to the board query's polling if the hub cannot be reached.
 */
export function useCycleLive(cycleId: Guid | undefined): LiveState {
  const qc = useQueryClient();
  const [state, setState] = useState<LiveState>('connecting');
  const qcRef = useRef(qc);
  qcRef.current = qc;

  useEffect(() => {
    if (!cycleId) return;
    let disposed = false;
    const onState = (s: LiveState) => !disposed && setState(s);
    stateListeners.add(onState);
    acquire();
    joinedCycles.set(cycleId, (joinedCycles.get(cycleId) ?? 0) + 1);

    const handler = (evt: BoardChangedEvent) => {
      if (evt.cycleId !== cycleId) return;
      const client = qcRef.current;
      void client.invalidateQueries({ queryKey: qk.cycles.board(cycleId) });
      void client.invalidateQueries({ queryKey: qk.reviewQueue.all });
      if (evt.executionId) {
        void client.invalidateQueries({ queryKey: qk.executions.review(evt.executionId) });
        void client.invalidateQueries({ queryKey: qk.executions.detail(evt.executionId) });
      }
      if (evt.kind === 'bug') void client.invalidateQueries({ queryKey: ['bugs', 'list'] });
    };

    const conn = getConnection();
    conn.on('boardChanged', handler);
    ensureStarted()
      .then((c) => (disposed ? undefined : c.invoke('JoinCycle', cycleId)))
      .then(() => !disposed && setState('connected'))
      .catch(() => !disposed && setState('disconnected'));

    return () => {
      disposed = true;
      stateListeners.delete(onState);
      conn.off('boardChanged', handler);
      const left = (joinedCycles.get(cycleId) ?? 1) - 1;
      if (left <= 0) {
        joinedCycles.delete(cycleId);
        if (conn.state === HubConnectionState.Connected) void conn.invoke('LeaveCycle', cycleId).catch(() => undefined);
      } else joinedCycles.set(cycleId, left);
      release();
    };
  }, [cycleId]);

  return state;
}
